// SSR-safe localStorage utility
// Provides safe access to localStorage that won't break during SSR or in non-browser environments

const isBrowser = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

/**
 * SSR-safe localStorage wrapper
 */
export const storage = {
  /**
   * Get an item from localStorage
   */
  getItem(key: string): string | null {
    if (!isBrowser) return null;
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },

  /**
   * Set an item in localStorage
   */
  setItem(key: string, value: string): void {
    if (!isBrowser) return;
    try {
      localStorage.setItem(key, value);
    } catch {
      // Storage might be full or disabled
    }
  },

  /**
   * Remove an item from localStorage
   */
  removeItem(key: string): void {
    if (!isBrowser) return;
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore errors
    }
  },

  /**
   * Clear all localStorage
   */
  clear(): void {
    if (!isBrowser) return;
    try {
      localStorage.clear();
    } catch {
      // Ignore errors
    }
  },
};

/**
 * Check if running in browser environment
 */
export const isClient = isBrowser;

/**
 * Safe window access for event listeners
 */
export const safeWindow = {
  addEventListener: (
    event: string,
    handler: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions
  ): void => {
    if (!isBrowser) return;
    window.addEventListener(event, handler, options);
  },

  removeEventListener: (
    event: string,
    handler: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions
  ): void => {
    if (!isBrowser) return;
    window.removeEventListener(event, handler, options);
  },
};
